import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Match } from '../types';
import './MatchCard.css';

function TeamLogo({ src, name }: { src: string; name: string }) {
  const [failed, setFailed] = useState(!src);
  const abbr = <span className="team-logo-abbr">{name.slice(0, 3).toUpperCase()}</span>;
  if (failed) return <div className="team-emblem">{abbr}</div>;
  return (
    <div className="team-emblem">
      <img
        src={src}
        alt={name}
        className="team-logo-img"
        onError={() => setFailed(true)}
        onLoad={e => {
          const img = e.currentTarget;
          if (img.naturalWidth < 8) setFailed(true);
        }}
      />
    </div>
  );
}

function PickLogo({ src, name }: { src: string; name: string }) {
  const [failed, setFailed] = useState(!src);
  if (failed) return <span className="pick-logo-abbr">{name.slice(0, 3).toUpperCase()}</span>;
  return (
    <img
      src={src}
      alt={name}
      className="pick-logo-img"
      onError={() => setFailed(true)}
      onLoad={e => {
        if (e.currentTarget.naturalWidth < 8) setFailed(true);
      }}
    />
  );
}

interface Props {
  match: Match;
  userPick?: 'home' | 'draw' | 'away';
  onPick: (matchId: string, pick: 'home' | 'draw' | 'away', meta?: { revealed: boolean; challenged: boolean; aiChoice: string }) => void;
  loading?: boolean;
  isGuest?: boolean;
}

function useCountdown(kickoffTime: string) {
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    const update = () => {
      const diff = new Date(kickoffTime).getTime() - Date.now();
      if (diff <= 0) { setCountdown('LIVE'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (h > 23) {
        const d = Math.floor(h / 24);
        setCountdown(`${d}d ${h % 24}h`);
      } else {
        setCountdown(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
      }
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [kickoffTime]);

  return countdown;
}

export default function MatchCard({ match, userPick, onPick, loading, isGuest }: Props) {
  const countdown = useCountdown(match.kickoffTime);
  const { aiPrediction: ai } = match;

  const [localPick, setLocalPick] = useState<'home' | 'draw' | 'away' | null>(null);
  const [predMode, setPredMode] = useState<'blind' | 'reveal' | null>(null);
  const [aiRevealed, setAiRevealed] = useState(false);
  const [aiEverRevealed, setAiEverRevealed] = useState(false);
  const [stance, setStance] = useState<'agree' | 'challenge' | null>(null);
  const [localLocked, setLocalLocked] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const isLocked = !!userPick || localLocked;
  const activePick = userPick ?? (localLocked ? localPick : null);

  const toggleSection = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleTeamSelect = (pick: 'home' | 'draw' | 'away') => {
    if (isLocked) return;
    setLocalPick(pick);
    // If AI was already revealed, keep reveal mode so stance buttons re-appear;
    // blind mode becomes permanently unavailable once AI is seen
    if (!aiEverRevealed) setPredMode(null);
    setStance(null);
  };

  const handleRevealAI = () => {
    setPredMode('reveal');
    setAiRevealed(true);
    setAiEverRevealed(true);
    setStance(null);
  };

  const handleLock = () => {
    if (!localPick) return;
    const meta = { revealed: aiEverRevealed, challenged: stance === 'challenge', aiChoice: ai.tip };
    onPick(match.matchId, localPick, meta);
    setLocalLocked(true);
  };

  const pickLabel = (pick: 'home' | 'draw' | 'away' | null) => {
    if (!pick) return '';
    if (pick === 'home') return `${match.homeTeam} Win`;
    if (pick === 'away') return `${match.awayTeam} Win`;
    return 'Draw';
  };

  const hasRichAnalysis = !!(ai.formGuide || ai.starPlayer || ai.headToHead || ai.keyStats);

  return (
    <div className={`match-card${isLocked ? ' is-locked' : ''}`}>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="match-card-header">
        <span className="competition-badge">{match.competition}</span>
        <div className="match-card-header-right">
          {isLocked && <span className="locked-badge">🔒 Locked</span>}
          <span className={`countdown${countdown === 'LIVE' ? ' live' : ''}`}>
            {countdown === 'LIVE' ? '🔴 LIVE' : `⏱ ${countdown}`}
          </span>
        </div>
      </div>

      {/* ── Teams ──────────────────────────────────────────────────── */}
      <div className="match-teams">
        <div className="team home">
          <TeamLogo src={match.homeTeamLogo} name={match.homeTeam} />
          <div className="team-name">{match.homeTeam}</div>
          {(aiRevealed || isLocked) && <div className="team-pct">{ai.homeWinPct}%</div>}
        </div>

        <div className="match-vs">
          <span className="vs-text">VS</span>
          <span className="venue-text">{match.venue}</span>
        </div>

        <div className="team away">
          <TeamLogo src={match.awayTeamLogo} name={match.awayTeam} />
          <div className="team-name">{match.awayTeam}</div>
          {(aiRevealed || isLocked) && <div className="team-pct">{ai.awayWinPct}%</div>}
        </div>
      </div>

      {/* ── AI Analysis ────────────────────────────────────────────── */}
      <div className="ai-analysis">

        {/* Confidence + probabilities — only after reveal or lock */}
        {(aiRevealed || isLocked) ? (
          <div className="ai-meta-row">
            <div className="confidence-bar-wrap">
              <div className="confidence-bar-track">
                <div className="confidence-bar-fill" style={{ width: `${ai.confidence}%` }} />
              </div>
              <span className="confidence-pct">{ai.confidence}% confidence</span>
            </div>
            <div className="prob-breakdown">
              <span className="prob home-prob">{match.homeTeam.slice(0, 3).toUpperCase()} {ai.homeWinPct}%</span>
              <span className="prob draw-prob">DRAW {ai.drawPct}%</span>
              <span className="prob away-prob">{match.awayTeam.slice(0, 3).toUpperCase()} {ai.awayWinPct}%</span>
            </div>
          </div>
        ) : (
          <div className="ai-meta-locked">
            <span className="ai-meta-locked-text">🤖 Pick blind to earn +15 pts · Reveal AI to see the analysis</span>
          </div>
        )}

        {/* AI pick — hidden until revealed or locked */}
        <div className={`ai-pick-row${aiRevealed || (isLocked && activePick) ? ' ai-pick-revealed' : ''}`}>
          <span className="ai-pick-label">🤖 AI Prediction</span>
          {aiRevealed || isLocked ? (
            <span className={`ai-tip${stance === 'challenge' ? ' tip-challenged' : ''}`}>{ai.tip}</span>
          ) : (
            <span className="ai-tip-hidden">Choose Blind or Reveal →</span>
          )}
        </div>

        {/* Form Guide — ALWAYS visible */}
        {ai.formGuide && (
          <div className="ai-section">
            <div className="ai-section-title">🔥 Form Guide</div>
            <div className="ai-form-guide">
              <p><strong>{match.homeTeam}:</strong> {ai.formGuide.home}</p>
              <p><strong>{match.awayTeam}:</strong> {ai.formGuide.away}</p>
            </div>
          </div>
        )}

        {/* Star Player — ALWAYS visible */}
        {ai.starPlayer && (
          <div className="ai-section">
            <div className="ai-section-title">⭐ Star Player Watch</div>
            <p className="ai-star-player">
              <strong>{ai.starPlayer.name}</strong> <span className="ai-star-team">({ai.starPlayer.team})</span> — {ai.starPlayer.insight}
            </p>
          </div>
        )}

        {/* Premium sections: sign-in required */}
        {hasRichAnalysis && (
          isGuest ? (
            <div className="ai-locked-wall">
              <div className="ai-locked-blur-bg">
                <div className="ai-section"><div className="ai-section-title">🏆 Head to Head</div><p className="ai-section-placeholder">Historical record between these teams...</p></div>
                <div className="ai-section"><div className="ai-section-title">📊 Key Stats</div><p className="ai-section-placeholder">3 match-specific data points...</p></div>
                <div className="ai-section"><div className="ai-section-title">⚠️ Watch Out</div><p className="ai-section-placeholder">Main risk to the AI prediction...</p></div>
              </div>
              <div className="ai-locked-overlay">
                <span className="ai-lock-icon">🔒</span>
                <span>Sign in to see full AI analysis</span>
                <Link to="/login" className="guest-lock-btn">Sign In Free</Link>
              </div>
            </div>
          ) : (
            <>
              {ai.headToHead && (
                <div className="ai-section ai-section-expandable">
                  <button className="ai-section-toggle" onClick={() => toggleSection('h2h')}>
                    <span>🏆 Head to Head</span>
                    <span className="ai-toggle-icon">{expanded.has('h2h') ? '▲' : '▼'}</span>
                  </button>
                  {expanded.has('h2h') && <p className="ai-section-body">{ai.headToHead}</p>}
                </div>
              )}
              {ai.keyStats && ai.keyStats.length > 0 && (
                <div className="ai-section ai-section-expandable">
                  <button className="ai-section-toggle" onClick={() => toggleSection('stats')}>
                    <span>📊 Key Stats</span>
                    <span className="ai-toggle-icon">{expanded.has('stats') ? '▲' : '▼'}</span>
                  </button>
                  {expanded.has('stats') && (
                    <ul className="ai-stats-list">
                      {ai.keyStats.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  )}
                </div>
              )}
              {ai.watchOut && (
                <div className="ai-section ai-section-expandable">
                  <button className="ai-section-toggle" onClick={() => toggleSection('watchOut')}>
                    <span>⚠️ Watch Out</span>
                    <span className="ai-toggle-icon">{expanded.has('watchOut') ? '▲' : '▼'}</span>
                  </button>
                  {expanded.has('watchOut') && <p className="ai-section-body">{ai.watchOut}</p>}
                </div>
              )}
            </>
          )
        )}

        {/* Why AI Might Be Wrong — only after prediction locked */}
        {isLocked && ai.whyImWrong && (
          <div className="ai-why-wrong">
            <div className="ai-why-wrong-header">🤔 Why AI Might Be Wrong</div>
            <p className="ai-why-wrong-reveal">Prediction locked. Now see what the AI is worried about →</p>
            <p className="ai-why-wrong-body">{ai.whyImWrong}</p>
          </div>
        )}
      </div>

      {/* ── Prediction Section ─────────────────────────────────────── */}
      {isGuest ? (
        <div className="pick-section-guest">
          <span className="guest-lock-icon">🔒</span>
          <p className="guest-lock-text">Sign in to predict and earn points</p>
          <Link to="/login" className="guest-lock-btn">Sign In</Link>
        </div>
      ) : isLocked ? (
        <div className="pick-locked-display">
          <span className="lock-icon">🔒</span>
          <span className="locked-pick-text">
            <strong>{pickLabel(activePick)}</strong>
          </span>
          <span className={`locked-mode-badge${predMode === 'blind' ? ' badge-blind' : stance === 'challenge' ? ' badge-challenge' : ' badge-follow'}`}>
            {predMode === 'blind' ? '🧠 Blind' : stance === 'challenge' ? '⚔️ Challenge' : '✓ Follow'}
          </span>
        </div>
      ) : (
        <div className="pick-section">
          <p className="pick-label">Your Prediction</p>

          {/* Team pick buttons */}
          <div className="pick-buttons">
            <button
              className={`pick-btn pick-home${localPick === 'home' ? ' selected' : ''}`}
              onClick={() => handleTeamSelect('home')}
              disabled={loading}
            >
              <PickLogo src={match.homeTeamLogo} name={match.homeTeam} />
              <span className="pick-team-name">{match.homeTeam}</span>
              <span className="pick-side-tag pick-side-home">{match.venue.split(',')[0]}</span>
            </button>

            <button
              className={`pick-btn pick-draw${localPick === 'draw' ? ' selected' : ''}`}
              onClick={() => handleTeamSelect('draw')}
              disabled={loading}
            >
              <span className="pick-draw-icon">=</span>
              <span className="pick-team-name">Draw</span>
              <span className="pick-side-tag pick-side-draw">
                {(aiRevealed || isLocked) ? `${ai.drawPct}%` : '—'}
              </span>
            </button>

            <button
              className={`pick-btn pick-away${localPick === 'away' ? ' selected' : ''}`}
              onClick={() => handleTeamSelect('away')}
              disabled={loading}
            >
              <PickLogo src={match.awayTeamLogo} name={match.awayTeam} />
              <span className="pick-team-name">{match.awayTeam}</span>
              <span className="pick-side-tag pick-side-away">Away</span>
            </button>
          </div>

          {/* Mode selection — shows after team pick */}
          {localPick && (
            <div className="pred-mode-wrap">
              <div className="pred-mode-label">Choose your mode:</div>
              <div className="pred-mode-buttons">
                <button
                  className={`pred-mode-btn${predMode === 'blind' ? ' active' : ''}${aiEverRevealed ? ' disabled' : ''}`}
                  onClick={() => { if (!aiEverRevealed) { setPredMode('blind'); setStance(null); } }}
                  disabled={aiEverRevealed}
                  title={aiEverRevealed ? 'You already revealed the AI pick' : undefined}
                >
                  <span className="pred-mode-icon">🧠</span>
                  <span className="pred-mode-name">Blind Mode</span>
                  <span className="pred-mode-pts">{aiEverRevealed ? '🚫 AI already seen' : '+15 if correct'}</span>
                </button>
                <button
                  className={`pred-mode-btn${predMode === 'reveal' ? ' active' : ''}`}
                  onClick={handleRevealAI}
                >
                  <span className="pred-mode-icon">👁</span>
                  <span className="pred-mode-name">Reveal AI Pick</span>
                  <span className="pred-mode-pts">See then decide</span>
                </button>
              </div>
            </div>
          )}

          {/* Agree / Challenge — shows after revealing AI */}
          {predMode === 'reveal' && aiRevealed && (
            <div className="stance-wrap">
              <div className="stance-label">
                AI predicts: <strong className="ai-revealed-tip">{ai.tip}</strong>
              </div>
              <div className="stance-buttons">
                <button
                  className={`stance-btn stance-agree${stance === 'agree' ? ' active' : ''}`}
                  onClick={() => setStance('agree')}
                >
                  ✓ Agree
                  <span className="stance-pts">+3 if correct, -5 if wrong</span>
                </button>
                <button
                  className={`stance-btn stance-challenge${stance === 'challenge' ? ' active' : ''}`}
                  onClick={() => setStance('challenge')}
                >
                  ⚔️ Challenge
                  <span className="stance-pts">+12 if correct, -2 if wrong</span>
                </button>
              </div>
            </div>
          )}

          {/* Confirmation panel — shows when ready to lock */}
          {localPick && (predMode === 'blind' || (predMode === 'reveal' && stance)) && (() => {
            const isBlind = predMode === 'blind';
            const isChallenge = stance === 'challenge';
            const isFollow = stance === 'agree';
            const modeLabel = isBlind ? '🧠 Blind' : isChallenge ? '⚔️ Challenge' : '✓ Follow AI';
            const ptsCorrect = isBlind ? '+15 pts' : isChallenge ? '+12 pts' : '+3 pts';
            const ptsWrong = isBlind ? '0 pts' : isChallenge ? '−2 pts' : '−5 pts';
            const ptsCorrectColor = isBlind ? 'confirm-pts-gold' : isChallenge ? 'confirm-pts-green' : 'confirm-pts-dim';
            const ptsWrongColor = isBlind ? 'confirm-pts-neutral' : 'confirm-pts-red';
            return (
              <div className="confirm-panel">
                <div className="confirm-summary">
                  <div className="confirm-pick-row">
                    <span className="confirm-pick-label">Your pick</span>
                    <span className="confirm-pick-value">{pickLabel(localPick)}</span>
                  </div>
                  <div className="confirm-pick-row">
                    <span className="confirm-pick-label">Mode</span>
                    <span className="confirm-mode-badge">{modeLabel}</span>
                  </div>
                  {!isBlind && (
                    <div className="confirm-pick-row">
                      <span className="confirm-pick-label">AI says</span>
                      <span className="confirm-ai-tip">{ai.tip}</span>
                    </div>
                  )}
                </div>
                <div className="confirm-pts-row">
                  <div className="confirm-pts-box">
                    <span className={`confirm-pts-val ${ptsCorrectColor}`}>{ptsCorrect}</span>
                    <span className="confirm-pts-desc">if correct</span>
                  </div>
                  <div className="confirm-pts-divider" />
                  <div className="confirm-pts-box">
                    <span className={`confirm-pts-val ${ptsWrongColor}`}>{ptsWrong}</span>
                    <span className="confirm-pts-desc">if wrong</span>
                  </div>
                </div>
                <button className="lock-btn" onClick={handleLock} disabled={loading}>
                  {loading ? '…' : '🔒 Confirm & Lock Prediction'}
                </button>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
